import PageLoader from "@/components/loader/PageLoader";
import BranchRequiredWrapper from "@/components/banners/BranchRequiredWrapper";
import { useUser } from "@/contexts/UserContext";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import { faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import PageHeader from "@/components/layouts/PageHeader";
import DataTable, { Column } from "@/components/layouts/DataTable";
import DeleteConfirmationDialog from "@/components/layouts/DeleteConfirmationDialog";
import { toast } from "sonner";

interface Device {
  id: number;
  name: string;
  device_number: string;
  status: string;
  mode: string;
  created_at: string;
  is_primary: boolean;
}

interface SortState {
  field: keyof Device;
  direction: "asc" | "desc";
}

interface DevicesTableProps {
  onMenuItemClick: (
    componentName: string,
    successMessage?: string,
    selectedItemId?: number,
  ) => void;
  successMessage: string;
}

export default function DevicesTable({
  onMenuItemClick,
  successMessage,
}: DevicesTableProps) {
  const { t } = useTranslation();
  const itemsPerPage = 10;
  const [devices, setDevices] = useState<Device[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [sortState, setSortState] = useState<SortState>({
    field: "name",
    direction: "asc",
  });
  const { user } = useUser();
  const [axiosInstance, loading, isFetching] = useAxiosInstance();
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<number | null>(null);

  const columns: Column<Device>[] = [
    {
      key: "name",
      header: t("back.management.business.device.table.name"),
      sortable: true,
      bold: true,
    },
    {
      key: "device_number",
      header: t("back.management.business.device.table.deviceNumber"),
      sortable: true,
      bold: true,
    },
    {
      key: "status",
      header: t("back.management.business.device.table.status"),
      sortable: true,
      bold: true,
    },
    {
      key: "mode",
      header: t("back.management.business.device.table.mode"),
      sortable: true,
      bold: true,
    },
    {
      key: "is_primary",
      header: t("back.management.business.device.table.isPrimary"),
      sortable: true,
      bold: true,
    },
    {
      key: "action",
      header: t("back.management.menu.product.table.action"),
      render: (device: Device) => (
        <>
          <button onClick={() => editItem(device.id)} className="mr-2">
            <FontAwesomeIcon
              icon={faEdit}
              className="text-yellow-600 hover:text-yellow-800"
            />
          </button>
          <button onClick={() => openDeleteConfirmation(device.id)}>
            <FontAwesomeIcon
              icon={faTrash}
              className="text-red-600 hover:text-red-800"
            />
          </button>
        </>
      ),
    },
  ];

  const getStatusColor = (device: Device) => {
    return device.status === "Active" ? "bg-green-500" : "bg-gray-500";
  };

  const renderMobileCard = (device: Device) => (
    <div className="flex items-center">
      <div className="flex flex-col">
        <p className="font-bold text-gray-900">{device.name}</p>
        <p className="text-gray-700">Device#: {device.device_number}</p>
        <p className="font-bold text-gray-900">
          Primary: {device.is_primary ? "Yes" : "No"}
        </p>
      </div>
      <div
        className={`absolute bottom-0 left-0 top-0 w-1.5 ${getStatusColor(
          device,
        )}`}
      ></div>
    </div>
  );

  const fetchDevices = useCallback(async () => {
    try {
      if (!loading && axiosInstance && user?.selectedBranch) {
        const apiUrl = `${import.meta.env.VITE_APP_API_BASE_URL}device/`;

        const response = await axiosInstance.get(
          `${apiUrl}?branch_id=${user.selectedBranch.id}&_page=${currentPage}&_limit=${itemsPerPage}`,
        );
        const fetchedDevices = response.data.results;
        setDevices(fetchedDevices);
        setTotalItems(response.data.count);
      }
    } catch (error) {
      console.error("Error fetching devices:", error);
      toast.error(t("back.management.business.device.failFetch"));
    }
  }, [loading, axiosInstance, user, currentPage, itemsPerPage, t]);

  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
    }
  }, [successMessage]);

  useEffect(() => {
    fetchDevices();
  }, [currentPage, itemsPerPage]);
  useEffect(() => {
    if (user?.selectedBranch) {
      fetchDevices();
    }
  }, [sortState, user?.selectedBranch, fetchDevices]);

  const editItem = (id: number) => {
    onMenuItemClick("editDevice", "", id);
  };

  const openDeleteConfirmation = (id: number) => {
    setItemToDelete(id);
    setDeleteConfirmationOpen(true);
  };

  const closeDeleteConfirmation = () => {
    setItemToDelete(null);
    setDeleteConfirmationOpen(false);
  };

  const confirmDelete = async () => {
    if (itemToDelete) {
      try {
        if (!loading && axiosInstance) {
          const apiUrl = `${import.meta.env.VITE_APP_API_BASE_URL}device/`;

          await axiosInstance.delete(`${apiUrl}${itemToDelete}`);
          fetchDevices();
          toast.success(t("back.management.business.device.successDelete"));
        }
      } catch (error) {
        console.error("Error deleting device:", error);
        toast.error(t("back.management.business.device.failDelete"));
      }
      closeDeleteConfirmation();
    }
  };

  const handleSortChange = (field: keyof Device) => {
    setSortState((prevState) => ({
      field,
      direction:
        prevState.field === field && prevState.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  return (
    <PageLoader isFetching={isFetching}>
      <BranchRequiredWrapper>
        <div className="container mx-auto px-4 py-2">
          <h1 className="mb-4 text-2xl font-bold text-gray-900">
            {t("back.management.business.device.title")}
          </h1>

          <DeleteConfirmationDialog
            isOpen={deleteConfirmationOpen}
            onClose={closeDeleteConfirmation}
            onConfirm={confirmDelete}
            title={t("back.management.business.device.confirmDelete")}
            message={t("back.management.business.device.confirmDeleteMessage")}
          />

          <div className="desktop-layout">
            <DataTable
              data={devices}
              columns={columns}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              totalItems={totalItems}
              sortState={sortState}
              onSortChange={handleSortChange}
              onPageChange={handlePageChange}
              onEdit={editItem}
              onDelete={openDeleteConfirmation}
              getStatusColor={getStatusColor}
              renderMobileCard={renderMobileCard}
            />
          </div>

          {/* Mobile Layout */}
          <div className="mobile-layout">
            <DataTable
              data={devices}
              columns={columns}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              totalItems={totalItems}
              sortState={sortState}
              onSortChange={handleSortChange}
              onPageChange={handlePageChange}
              onEdit={editItem}
              onDelete={openDeleteConfirmation}
              getStatusColor={getStatusColor}
              renderMobileCard={renderMobileCard}
            />
          </div>
        </div>
      </BranchRequiredWrapper>
    </PageLoader>
  );
}
